import { OK_200, OK_201 } from 'common/config/api';
import { clientWithAuth } from '../base';
import { attioEndpoints } from '../endpoints';
import { attioCache } from './AttioCacheService';

/**
 * Base class for all Attio services
 * Provides common functionality and API client access
 */
export abstract class BaseAttioService {
  protected readonly client = clientWithAuth;
  protected readonly endpoints = attioEndpoints;
  protected readonly OK_STATUS = OK_200;
  protected readonly OK_STATUS_201 = OK_201;
  protected readonly cache = attioCache;
  protected readonly serviceName: string;

  constructor(serviceName: string) {
    this.serviceName = serviceName;
  }

  /**
   * Generic method to make GET requests
   */
  protected async get<T>(endpoint: string, signal?: AbortSignal): Promise<T> {
    const response = await this.client.get(endpoint, { signal });
    if (response.status === Number(this.OK_STATUS)) {
      return response.data;
    }
    throw new Error(response.data || response.statusText);
  }

  /**
   * Generic method to make POST requests
   */
  protected async post<T>(endpoint: string, data: any, signal?: AbortSignal): Promise<T> {
    const response = await this.client.post(endpoint, data, { signal });
    if (response.status === Number(this.OK_STATUS) || response.status === Number(this.OK_STATUS_201)) {
      return response.data;
    }
    throw new Error(response.data || response.statusText);
  }

  /**
   * Generic method to make PATCH requests
   */
  protected async patch<T>(endpoint: string, data: any, signal?: AbortSignal): Promise<T> {
    const response = await this.client.patch(endpoint, data, { signal });
    if (response.status === Number(this.OK_STATUS)) {
      return response.data;
    }
    throw new Error(response.data || response.statusText);
  }

  /**
   * Generic method to make PUT requests
   */
  protected async put<T>(endpoint: string, data: any, signal?: AbortSignal): Promise<T> {
    const response = await this.client.put(endpoint, data, { signal });
    if (response.status === Number(this.OK_STATUS) || response.status === Number(this.OK_STATUS_201)) {
      return response.data;
    }
    throw new Error(response.data || response.statusText);
  }

  /**
   * Handle errors consistently across all services
   */
  protected handleError(error: unknown, context: string): never {
    console.error(`Error in ${context}:`, error);
    throw error;
  }

  /**
   * Get cached data or fetch and cache
   */
  protected async getCached<T>(
    method: string,
    params: Record<string, any>,
    fetchFn: () => Promise<T>,
    ttl?: number
  ): Promise<T> {
    const cacheKey = this.cache.generateKey(this.serviceName, method, params);

    // Try to get from cache first
    const cached = this.cache.get<T>(cacheKey);
    if (cached !== null) {
      return cached;
    }

    // Fetch data and cache it
    const data = await fetchFn();
    this.cache.set(cacheKey, data, ttl);
    return data;
  }

  /**
   * Invalidate cache entries for a specific method
   */
  protected invalidateCache(method: string): void {
    const stats = this.cache.getStats();
    const keysToDelete = stats.keys.filter(key => key.startsWith(`${this.serviceName}:${method}:`));

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Clear all cache for this service
   */
  protected clearServiceCache(): void {
    const stats = this.cache.getStats();
    const keysToDelete = stats.keys.filter(key => key.startsWith(`${this.serviceName}:`));

    keysToDelete.forEach(key => this.cache.delete(key));
  }
}
